import React, { useState, useContext, useEffect } from 'react';
import { useIntl } from 'umi';
import { Table, TableColumn } from 'tea-component';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import TableCountryIcon from '@/pages/intelligence/components/TableComponents/components/TableCountryIcon';
import useSubTable from './hooks/useSubTable';
import { StoreRankDetailsCoreContext } from '../../context';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { getCountryNameByCountryCodeSimple } from '@/pages/intelligence/utils/commonUtils';
import useCountriesNames from '@intelligence/hooks/useCountriesNames';

import type { IBaseProps } from '@/pages/intelligence/types/common';
import type {
  IStoreRankDetailsTablesSourceData,
  IStoreRankDetailsCountriesRanks,
  IStoreRankDetailsCountryRank,
} from '@/pages/intelligence/types/storeRank';
import type { IStoreRankDetailsCoreContext } from '../../types';

const { sortable, pageable } = Table.addons;

interface ISubTableProps extends IBaseProps {
  record: IStoreRankDetailsTablesSourceData;
  ranks?: IStoreRankDetailsCountryRank[];
}

export default function SubTable(props: ISubTableProps) {
  const { formatMessage } = useIntl();

  const { record, ranks = [{ rankCode: 'rank', rankName: formatMessage({ id: 'dbi_rank' }) }] } = props;

  const { findCountryLocalName } = useCountriesNames()
  const {
    showRankName,
    countries = [],
    fetchCountriesRankData,
  } = useContext<IStoreRankDetailsCoreContext>(StoreRankDetailsCoreContext);

  const [isLoading, setIsLoading] = useState(true);
  const [tableData, setTableData] = useState<IStoreRankDetailsCountriesRanks[]>([]);

  const columns: TableColumn<IStoreRankDetailsCountriesRanks>[] = (() => {
    const columnsTemp: TableColumn<IStoreRankDetailsCountriesRanks>[] = [
      {
        key: 'country',
        header: formatMessage({ id: 'dbi_countries' }),
        // width: 200,
        render: (cvm: IStoreRankDetailsCountriesRanks) => {
          // const countryName = cvm?.countryName || getCountryNameByCountryCodeSimple(countries, cvm?.countryCode);
          const countryName = findCountryLocalName(cvm?.countryName || cvm?.countryCode);
          const countryIcon = getCountryFlag(cvm?.countryCode);
          return <TableCountryIcon cvm={countryName} icon={countryIcon} />;
        },
      },
    ];
    if (!Array.isArray(ranks)) return columnsTemp;
    for (const rank of ranks) {
      const rankCode = rank?.rankCode;
      const rankName = rank?.rankName;
      if (!rankCode) continue;
      columnsTemp.push({
        key: rankCode,
        header: rankName,
        render: (cvm: IStoreRankDetailsCountriesRanks) => {
          const rankData = cvm?.ranks?.find((item) => item?.rankCode === rankCode);
          // const rankName = rankData?.rankName;
          const rankValue = rankData?.rankValue;
          if (rankValue === undefined || rankValue === null) return '-';
          return rankValue;
          // return showRankName && rankName ? `${rankName} ${rankValue}` : rankValue;
        },
      });
    }
    return columnsTemp;
  })();

  const { sortedTableData, pageableConfig, sortableConfig } = useSubTable(tableData);

  const fetchTableData = async () => {
    setIsLoading(true);
    try {
      const res = await fetchCountriesRankData?.(record);
      const tableDataTemp = Array.isArray(res) ? res : [];
      setTableData(tableDataTemp);
    } catch (err) {
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTableData();
  }, []);

  return (
    <Abnormal
      style={{ height: 200 }}
      isLoading={isLoading}
      isNoData={!isLoading && !tableData?.length}
      component={
        <Table
          records={sortedTableData}
          bordered={true}
          columns={columns}
          addons={[
            // @ts-ignore
            sortable(sortableConfig),
            pageable(pageableConfig),
          ]}
        />
      }
    />
  );
}
